import {useFileUpload} from '@/hooks/useFileUpload'
import {processPromotionFile} from '@/services/PromotionsService/promotions'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {Constants} from '@/utils/constants/commonConstants'
import {getBulkPromotionErrors, VALIDATION_STATUS} from '@/utils/promotions/bulkPromotions'
import {useQueryClient} from '@tanstack/react-query'
import {useCallback, useEffect, useState} from 'react'

export const useBulkPromotion = file => {
  const {uploadFile} = useFileUpload()
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const [fileId, setFileId] = useState('')

  useEffect(() => {
    setFileId('')
  }, [file])

  const uploadBulkPromotionFile = useCallback(
    async (onCloseModal, setUploading, setFeedback) => {
      setUploading(true)
      if (!file) return

      try {
        const res = await uploadFile(file, Constants.bulkUploadPromotions, true)
        const validPromotionsCount = res?.validationResult?.validPromotions ?? 0
        setFileId(res?.fileUploadUUID)
        if (res?.validationResult?.validationStatus === VALIDATION_STATUS.VALID) {
          setFeedback(
            <div className="$font-small-bold">{`${validPromotionsCount} promotion(s) passed validation successfully`}</div>,
            Constants.Validated
          )
        } else {
          // If there are valid promotions, show a warning with error details. If there are no valid promotions, show an error with the same details.
          const hasValidPromotions = validPromotionsCount > 0
          const errorMessages = getBulkPromotionErrors(res?.validationResult)
          setFeedback(errorMessages, hasValidPromotions ? Constants.Warning : Constants.Error)
        }
      } catch (err) {
        setFeedback(err?.data?.message ?? Constants.ErrorForFileUpload)
      } finally {
        setUploading(false)
      }
    },
    [file, uploadFile]
  )

  const processBulkPromotionFile = useCallback(
    async (onCloseModal, setUploading, setFeedback) => {
      if (!fileId) return
      setUploading(true)
      try {
        await processPromotionFile(fileId)
        await queryClient.invalidateQueries({queryKey: ['PROMOTIONS_UPLOAD_HISTORY']})
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Bulk promotion file is being processed. You can check the status in Upload History.',
            type: 'success',
          })
        )
        onCloseModal()
      } catch (err) {
        setFeedback(
          err?.data?.message ?? 'There was an error processing the bulk promotion file. Please try again',
          Constants.Warning
        )
      } finally {
        setUploading(false)
      }
    },
    [fileId, queryClient, dispatch]
  )

  const handleBulkPromotionFile = useCallback(
    async (onCloseModal, setUploading, setFeedback) => {
      if (fileId) {
        await processBulkPromotionFile(onCloseModal, setUploading, setFeedback)
      } else {
        await uploadBulkPromotionFile(onCloseModal, setUploading, setFeedback)
      }
    },
    [fileId, processBulkPromotionFile, uploadBulkPromotionFile]
  )

  return {
    handleBulkPromotionFile,
  }
}
